import axios from 'axios';
import i18n from '../language/i18n';

const t = (key: string) => i18n.t(`AddRecords.automatedImport.${key}`, { ns: 'traffic' });

const REACT_APP_API_URL_DN = process.env.REACT_APP_API_URL_DN || 'https://localhost:7111/api';

export interface ImportHistoryRecord {
  id: number;
  rok: number;
  datumImportu: string;
  stav: string;
  celkovyPocetZaznamov: number | null;
  vlozeneZaznamy: number | null;
  preskoceneZaznamy: number | null;
  chyboveZaznamy: number | null;
  celkovyPocetChodcov: number | null;
  vlozeniChodci: number | null;
  preskoceniChodci: number | null;
  chyboviChodci: number | null;
  partitionCreated: boolean;
  partitionRok: number | null;
  errorExcelAvailable: boolean;
  errorExcelFilename: string | null;
  notificationEmail: string | null;
  casSpustenia: string | null;
  casUkoncenia: string | null;
}

const automatedImportService = {
  getLatestImport: async (): Promise<ImportHistoryRecord | null> => {
    try {
      const response = await axios.get<ImportHistoryRecord>(
        `${REACT_APP_API_URL_DN}/AutomatedImport/latest`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 204) return null;
      throw new Error(
        error.response?.data?.error || error.message || t('errorLoadLatestImport')
      );
    }
  },

  getImportHistory: async (limit = 20): Promise<ImportHistoryRecord[]> => {
    try {
      const response = await axios.get<ImportHistoryRecord[]>(
        `${REACT_APP_API_URL_DN}/AutomatedImport/history`,
        { params: { limit } }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || error.message || t('errorLoadHistory')
      );
    }
  },

  downloadErrorExcel: async (importId: number, fileName?: string): Promise<void> => {
    try {
      const response = await fetch(
        `${REACT_APP_API_URL_DN}/AutomatedImport/${importId}/download-errors`
      );

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `failed_inserts_${importId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      throw new Error(error.message || t('errorDownloadExcel'));
    }
  },

  triggerManualImport: async (year: number, email?: string): Promise<void> => {
    try {
      const params = email ? { email } : {};
      await axios.post(`${REACT_APP_API_URL_DN}/AutomatedImport/trigger/${year}`, null, { params });
    } catch (error: any) {
      if (error.response?.status === 409)
        throw new Error(i18n.t('AddRecords.automatedImport.alreadyRunning', { ns: 'traffic', year }) as string);
      throw new Error(
        error.response?.data?.error || error.message || t('triggerError')
      );
    }
  },

  getNotificationEmail: async (): Promise<string> => {
    try {
      const response = await axios.get<{ email: string }>(
        `${REACT_APP_API_URL_DN}/AutomatedImport/notification-email`
      );
      return response.data.email ?? '';
    } catch {
      return '';
    }
  },

  saveNotificationEmail: async (email: string): Promise<void> => {
    try {
      await axios.put(`${REACT_APP_API_URL_DN}/AutomatedImport/notification-email`, { email });
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || error.message || t('errorSaveEmail')
      );
    }
  },

  getScheduleMonthly: async (): Promise<boolean> => {
    try {
      const response = await axios.get<{ scheduleMonthly: boolean }>(
        `${REACT_APP_API_URL_DN}/AutomatedImport/schedule-monthly`
      );
      return response.data.scheduleMonthly ?? false;
    } catch {
      return false;
    }
  },

  saveScheduleMonthly: async (scheduleMonthly: boolean): Promise<void> => {
    try {
      await axios.put(`${REACT_APP_API_URL_DN}/AutomatedImport/schedule-monthly`, { scheduleMonthly });
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || error.message || t('errorSaveScheduleMonthly')
      );
    }
  },

  cancelImport: async (): Promise<void> => {
    try {
      await axios.post(`${REACT_APP_API_URL_DN}/AutomatedImport/cancel`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || error.message || t('cancelError')
      );
    }
  },
};

export default automatedImportService;